import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { supabase } from "@/lib/supabase";
import i18n from '../i18n';
import { Capacitor } from '@capacitor/core';
import { resetAnalyticsData } from '@/lib/firebaseAnalytics';
import { rememberReportedTimezone, timezoneToReport } from '@/lib/deviceTimezone';

// GDPR — when the server flags a user as having withdrawn analytics
// consent, disable analytics on this device and reset the Firebase
// App Instance ID so past events can't be re-linked. Best-effort.
async function _enforceAnalyticsOptOut(profile) {
  if (profile && profile.analytics_opt_out) {
    try { await resetAnalyticsData(); } catch (_) { /* ignore */ }
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Always send cookies on every request so the HttpOnly legacy-auth cookie is
// included on page reload (withCredentials resets to false on every fresh JS
// context — setting it here once at module load ensures it is always true).
axios.defaults.withCredentials = true;

const UserContext = createContext();

// Hook personalizzato per accedere al contesto
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within UserProvider");
  }
  return context;
};

// Provider
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBetatester, setIsBetatester] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Debug: log platform and backend URL
  const isNative = Capacitor.isNativePlatform();
  console.log('[UserContext] Platform:', isNative ? 'Native' : 'Web');
  console.log('[UserContext] Backend API URL:', API);

  // DEBUG: Alert su mobile
  React.useEffect(() => {
    if (isNative) {
      //alert('[DEBUG] UserContext initialized on mobile');
    }
  }, [isNative]);

  // Debug: log backend URL
  console.log('[UserContext] Backend API URL:', API);

  // Get Supabase session token
  const getSupabaseToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  // Funzione per salvare token al login / onboarding (legacy -> HttpOnly cookie)
  // - Persist the legacy token in an HttpOnly cookie via the backend helper endpoint
  // - Do NOT store the token in localStorage anymore
  const saveToken = (newToken) => {
    // Keep token in memory immediately so components can react synchronously
    setToken(newToken);
    // Persist to HttpOnly cookie (fire-and-forget); enable axios cookie mode on success
    // Use Promise.resolve(...) to be tolerant of mocked environments that may return
    // non-Promise values during tests.
    Promise.resolve(axios.post(`${API}/auth/legacy-cookie`, { token: newToken }, { withCredentials: true }))
      .then(() => { axios.defaults.withCredentials = true; })
      .catch((err) => { console.error('[UserContext] Failed to persist legacy token as cookie:', err); });
  };

  // Funzione per rimuovere token al logout
  const removeToken = useCallback(async () => {
    try {
      // Clear server-side HttpOnly cookie (best-effort)
      await axios.post(`${API}/auth/legacy-cookie/clear`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('[UserContext] clearing legacy cookie failed (continuing):', err);
    }

    // Clear Supabase session storage
    localStorage.removeItem('sb-ixfcdbyhpyxdnozixfiz-auth-token');
    await supabase.auth.signOut();
    // Clear in-memory state
    setToken(null);
    setUser(null);
    setIsPremium(false);
    setIsAdmin(false);
    setIsBetatester(false);
    setTrialExpired(false);
    setLoading(false);
  }, []);

  // Fetch dei dati utente
  // Memoized with useCallback for useEffect deps
  const fetchUserData = useCallback(async (tokenOverride) => {
    console.log('[UserContext] Starting fetchUserData...', tokenOverride ? '(with token override)' : '');
    // Se abbiamo già dati utente validi, non ricaricare
    if (user && user.user_id) {
      console.log('[UserContext] User data already loaded, skipping fetch');
      return;
    }
    try {
      setLoading(true);
      // Get Supabase token — use override when provided to avoid getSession() deadlock on native
      const supabaseToken = tokenOverride || await getSupabaseToken();
      console.log('[UserContext] Supabase token:', supabaseToken ? 'present' : 'null');
      
      let currentToken = supabaseToken;
      let useDefaults = false;
      
      if (!supabaseToken) {
        // Do NOT read legacy token from localStorage anymore.
        // Prefer axios defaults (Authorization / X-User-Id) or cookie-based auth.
        const authHeader = axios?.defaults?.headers?.common?.Authorization || null;
        const userIdHeader = axios?.defaults?.headers?.common?.['X-User-Id'] || null;
        console.log('[UserContext] Axios default auth header:', authHeader ? 'present' : 'null');
        console.log('[UserContext] Axios default X-User-Id header:', userIdHeader ? 'present' : 'null');

        if (authHeader || userIdHeader) {
          useDefaults = true;
          console.log('[UserContext] Using axios defaults for authentication');
        } else if (axios?.defaults?.withCredentials) {
          useDefaults = true;
          console.log('[UserContext] Using cookies for authentication');
        } else {
          console.log('[UserContext] No authentication method found, stopping fetch');
          setLoading(false);
          return;
        }
      } else {
        setToken(supabaseToken);
      }
      console.log('[UserContext] Making API call to /api/users/me');
      const requestConfig = useDefaults ? {} : {
        headers: { Authorization: `Bearer ${currentToken}` },
      };
      
      const res = await axios.get(`${API}/users/me`, requestConfig);
      console.log('[UserContext] API response:', res.data);
      if (res.data && res.data.profile) {
        setUser(res.data.profile);
        _enforceAnalyticsOptOut(res.data.profile);
        setIsAdmin(res.data.profile.role === "admin");
        setIsBetatester(res.data.profile.role === "betatester");
        // Admin e betatester hanno premium sempre attivo
        const subStatus = res.data.profile.subscription_status;
        setIsPremium(
          res.data.profile.role === "admin" || 
          res.data.profile.role === "betatester" || 
          subStatus === "premium" || 
          subStatus === "trial"
        );
        setTrialExpired(subStatus === "expired");

        // Sync language with i18n — prefer an explicit client choice (localStorage) so
        // an in-progress onboarding selection isn't overwritten by the server value.
        const savedLang = localStorage.getItem('betterhusbandUserLang');
        const profileLang = res.data.profile.language || null;

        // If the user is currently on the onboarding route, avoid overwriting their
        // in-progress selection. Prefer the client-saved language and skip forcing
        // a profile value while onboarding is in progress.
        const isOnboardingRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/onboarding');

        console.log('[UserContext] language sync — savedLang:', savedLang, 'profileLang:', profileLang, 'isOnboardingRoute:', isOnboardingRoute, 'i18n.language:', i18n.language);

        if (isOnboardingRoute) {
          if (savedLang && savedLang !== i18n.language) {
            await i18n.changeLanguage(savedLang);
          }
          // Do not overwrite savedLang with profileLang while onboarding
          return;
        }

        const userLanguage = savedLang || profileLang || i18n.language || 'en';
        console.log('[UserContext] elected language after precedence:', userLanguage);
        if (userLanguage !== i18n.language) {
          await i18n.changeLanguage(userLanguage);
        }
        // Persist whichever language we elected to use (keeps client/server in sync later)
        localStorage.setItem('betterhusbandUserLang', userLanguage);
      } else {
        setUser(null);
        setIsAdmin(false);
        setIsBetatester(false);
        setIsPremium(false);
      }
      
    } catch (err) {
      console.error("[UserContext] Error fetching user:", err.response?.data || err);
      
      if (err.response?.status === 401) {
        console.log('[UserContext] 401 error - attempting token refresh');
        try {
          // Try to refresh the session
          const { data, error } = await supabase.auth.refreshSession();
          if (data.session?.access_token && !error) {
            console.log('[UserContext] Token refreshed successfully, retrying fetch');
            setToken(data.session.access_token);
            // Retry the fetch with the new token
            const retryRes = await axios.get(`${API}/users/me`, {
              headers: { Authorization: `Bearer ${data.session.access_token}` },
            });
            console.log('[UserContext] Retry API response:', retryRes.data);
            setUser(retryRes.data.profile);
            _enforceAnalyticsOptOut(retryRes.data.profile);
            setIsAdmin(retryRes.data.profile.role === "admin");
            setIsBetatester(retryRes.data.profile.role === "betatester");
            setIsPremium(retryRes.data.profile.role === "admin" || retryRes.data.profile.role === "betatester" || retryRes.data.profile.subscription_status === "premium" || retryRes.data.profile.subscription_status === "trial");
            
            // If onboarding route is active, don't let the server language overwrite
            // a client choice in-progress.
            const savedLang = localStorage.getItem('betterhusbandUserLang');
            const profileLang = retryRes.data.profile.language || null;
            const isOnboardingRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/onboarding');

            if (isOnboardingRoute) {
              if (savedLang && savedLang !== i18n.language) {
                await i18n.changeLanguage(savedLang);
              }
              return;
            }

            const userLanguage = savedLang || profileLang || i18n.language || 'en';
            if (userLanguage !== i18n.language) {
              await i18n.changeLanguage(userLanguage);
            }
            localStorage.setItem('betterhusbandUserLang', userLanguage);
            setLoading(false);
            return;
          }
        } catch (refreshError) {
          console.error('[UserContext] Token refresh failed:', refreshError);
        }
        
        // Refresh failed — clear user state but do NOT call removeToken/signOut.
        // A transient 401 should not destroy the Supabase session and kick the
        // user back to login.  The onAuthStateChange listener or the next
        // manual fetchUserData call will recover once the session is valid again.
        console.warn('[UserContext] 401 recovery failed — leaving user null (session preserved)');
      }

      setUser(null);
      setIsAdmin(false);
      setIsBetatester(false);
      setIsPremium(false);
      setTrialExpired(false);
    } finally {
      setLoading(false);
    }
  }, [getSupabaseToken, user]);

  // Aggiorna user localmente
  const updateUser = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
    if (updates?.role) {
      setIsAdmin(updates.role === "admin");
      setIsBetatester(updates.role === "betatester");
      setIsPremium(updates.role === "admin" || updates.role === "betatester" || updates.subscription_status === "premium" || updates.subscription_status === "trial");
    } else if (updates?.subscription_status) {
      setIsPremium(updates.subscription_status === "premium" || updates.subscription_status === "trial");
    }
    if (updates?.subscription_status !== undefined) {
      setTrialExpired(updates.subscription_status === "expired");
    }
  };

  // Force fetch — bypasses rate limiter AND the user-exists early-exit
  // (used for auth state changes after login/re-login)
  const forceFetchUserData = React.useCallback(async (tokenOverride) => {
    // Clear cached user so fetchUserData doesn't skip via early-exit
    setUser(null);
    // Small yield so React flushes the null before we re-fetch
    await new Promise(r => setTimeout(r, 0));
    await fetchUserData(tokenOverride);
  }, [fetchUserData]);

  // Effetto iniziale: fetch se token presente, con timeout di sicurezza
  useEffect(() => {
    let isMounted = true;
    let lastFetchTime = 0;
    const MIN_FETCH_INTERVAL = 30000; // 30 seconds minimum between fetches
    let timeoutId;

    const safeFetchUserData = async (tokenOverride) => {
      const now = Date.now();
      if (now - lastFetchTime < MIN_FETCH_INTERVAL) {
        return; // Skip if fetched recently
      }
      lastFetchTime = now;
      if (isMounted) {
        await fetchUserData(tokenOverride);
        clearTimeout(timeoutId);
      }
    };

    // Timeout di sicurezza: forza loading a false dopo 8 secondi
    timeoutId = setTimeout(() => {
      if (isMounted) {
        console.log('[UserContext] Safety timeout reached, setting loading to false');
        setLoading(false);
      }
    }, 8000);

    // Ritarda il fetch per permettere al login di completare, ma solo se abbiamo una sessione valida
    const delayMs = Capacitor.isNativePlatform() ? 2000 : 500;
    console.log(`[UserContext] Delaying fetch check by ${delayMs}ms for platform: ${Capacitor.isNativePlatform() ? 'native' : 'web'}`);
    setTimeout(async () => {
      // Only proceed if there is an actual Supabase session or an explicit axios auth header.
      // Do NOT fall through just because withCredentials is true — that flag is always set
      // globally and would cause a spurious unauthenticated request every time the app loads.
      const { data: { session }, error } = await supabase.auth.getSession();
      const hasSupabaseSession = session?.access_token && !error;
      const hasAxiosAuth = (axios?.defaults?.headers?.common?.Authorization) || (axios?.defaults?.headers?.common?.['X-User-Id']);

      if (hasSupabaseSession || hasAxiosAuth) {
        console.log('[UserContext] Valid authentication found, proceeding with fetch', {
          supabase: !!hasSupabaseSession,
          axios: !!hasAxiosAuth
        });
        safeFetchUserData(session?.access_token || null);
      } else {
        console.log('[UserContext] No authentication method found, skipping fetch');
        setLoading(false);
      }
    }, delayMs);

    // Listen for auth state changes
    let hasFetchedOnInitialSession = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[UserContext] Auth state change:', event, !!session);
      if (!isMounted) return;

      if (event === 'INITIAL_SESSION' && !hasFetchedOnInitialSession) {
        hasFetchedOnInitialSession = true;
        if (session?.access_token) {
          setToken(session.access_token);
          await safeFetchUserData(session.access_token);
        }
      } else if (event === 'SIGNED_IN') {
        // Always fetch on SIGNED_IN — the user just authenticated (e.g. after Google OAuth).
        // Do NOT guard with hasFetchedOnInitialSession: that flag is set by the initial
        // Supabase session check which may have run before the OAuth flow completed,
        // so SIGNED_IN is the canonical "we now have a valid session" signal.
        if (session?.access_token) {
          setToken(session.access_token);
          await forceFetchUserData(session.access_token); // bypass rate limiter + pass token to avoid getSession deadlock
        }
      } else if (event === 'TOKEN_REFRESHED') {
        if (session?.access_token) {
          setToken(session.access_token);
        }
      } else if (event === 'SIGNED_OUT') {
        setToken(null);
        setUser(null);
        setIsPremium(false);
        setIsAdmin(false);
        setIsBetatester(false);
        setTrialExpired(false);
        setLoading(false);
        hasFetchedOnInitialSession = false;
        // Clear Supabase session storage
        localStorage.removeItem('sb-ixfcdbyhpyxdnozixfiz-auth-token');
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [fetchUserData, forceFetchUserData]);

  const value = {
    user,
    isPremium,
    isAdmin,
    isBetatester,
    trialExpired,
    loading,
    token,
    saveToken,
    removeToken,
    fetchUserData,
    forceFetchUserData,
    refreshUser: forceFetchUserData, // alias for convenience
    updateUser,
  };

  // Effect to sync language when user changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // If the user explicitly selected a language in the client (e.g. during
    // onboarding) prefer that over the stored profile value — do not overwrite
    // a recent client choice with the server profile while onboarding.
    const savedLang = localStorage.getItem('betterhusbandUserLang');
    console.log('[UserContext] user.language effect — savedLang:', savedLang, 'user.language:', user?.language, 'i18n.language:', i18n.language);

    if (savedLang) {
      // Client-side language preference always takes priority over server profile
      if (savedLang !== i18n.language) {
        i18n.changeLanguage(savedLang);
      }
      // If server profile disagrees with the client preference, push client lang to server
      // and update the local user object so other components see the correct language.
      const userId = user?.user_id || user?.id;
      if (userId && user?.language && user.language !== savedLang) {
        setUser(prev => prev ? { ...prev, language: savedLang } : prev);
        axios.put(`${API}/user/${userId}`, { language: savedLang }).catch(err => {
          console.warn('[UserContext] Failed to sync language to server:', err);
        });
      }
      return;
    }

    if (user?.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.language]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Keep the stored time zone on the phone's, so reminders arrive at the
  // chosen time where the user actually is (see lib/deviceTimezone.js).
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const userId = user?.user_id || user?.id;
    if (!userId) return;
    const zone = timezoneToReport(user.timezone);
    if (!zone) return;
    axios.put(`${API}/user/${userId}`, { timezone: zone })
      .then(() => {
        rememberReportedTimezone(zone);
        setUser(prev => (prev && (prev.user_id || prev.id) === userId ? { ...prev, timezone: zone } : prev));
      })
      .catch(err => console.warn('[UserContext] Failed to sync time zone to server:', err));
  }, [user?.user_id, user?.id, user?.timezone]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Effect to handle language changes globally
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      // Force re-render of components that depend on language
      if (user) {
        setUser({ ...user }); // Trigger re-render
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [user]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
